package components

import androidx.compose.runtime.Composable
import kotlinx.browser.window
import models.NewsItem
import navigation.newsDetailHash
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

/** HTMLタグを除去するユーティリティ */
fun stripHtmlTags(text: String): String = text.replace(Regex("<[^>]*>"), "").trim()

@Composable
fun NewsCard(newsItem: NewsItem) {
    Article(attrs = {
        classes("news-card")
        // カード全体タップで詳細ページへ。修飾キー付きクリック（Cmd/Ctrl+クリックで
        // 新規タブ等）はタイトル<a>の標準挙動に任せるため奪わない
        onClick { event ->
            if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                window.location.hash = newsDetailHash(newsItem.id)
            }
        }
    }) {
        Div(attrs = {
            classes("news-card-header")
        }) {
            H3(attrs = {
                classes("news-card-title")
            }) {
                A(href = newsDetailHash(newsItem.id)) {
                    Text(stripHtmlTags(newsItem.titleJa ?: newsItem.title))
                }
            }
            Span(attrs = {
                classes("news-card-category", "category-${newsItem.category.lowercase()}")
            }) {
                Text(newsItem.category)
            }
        }
        
        newsItem.description?.let { desc ->
            P(attrs = {
                classes("news-card-description")
            }) {
                val displayDesc = stripHtmlTags(newsItem.descriptionJa ?: desc)
                Text(displayDesc.take(150) + if (displayDesc.length > 150) "..." else "")
            }
        }
        
        Div(attrs = {
            classes("news-card-footer")
        }) {
            Span(attrs = {
                classes("news-card-date")
            }) {
                Text(formatDate(newsItem.publishedAt))
            }
            Span(attrs = {
                classes("news-card-meta")
            }) {
                Text("Source: ${newsItem.source}")
                if (newsItem.translationStatus == "completed") {
                    Span(attrs = {
                        classes("translation-badge")
                    }) {
                        Text(" 🇯🇵")
                    }
                }
            }
        }
    }
}

fun formatDate(dateString: String): String {
    return try {
        val date = Date(dateString)
        // Intl.DateTimeFormat で JST（Asia/Tokyo）に変換して表示
        val formatter = js("new Intl.DateTimeFormat('ja-JP', {timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'})")
        val formatted = (formatter.format(date) as String).replace("/", ".")
        "$formatted JST"
    } catch (e: dynamic) {
        dateString
    }
}

object NewsCardStyles : StyleSheet() {
    init {
        ".news-card" style {
            backgroundColor(rgba(30, 41, 59, 0.6))
            borderRadius(20.px)
            padding(1.75.em)
            property("border", "1px solid rgba(255,255,255,0.08)")
            property("backdrop-filter", "blur(16px)")
            property("box-shadow", "0 4px 24px rgba(0,0,0,0.25)")
            property("transition", "transform 0.2s ease, box-shadow 0.2s ease")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            height(100.percent)
            cursor("pointer")
        }

        ".news-card:hover" style {
            property("transform", "translateY(-2px)")
            property("box-shadow", "0 8px 32px rgba(0,0,0,0.35)")
        }

        ".news-card-header" style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Start)
            marginBottom(0.75.em)
        }

        ".news-card-title" style {
            flex(1)
            marginRight(1.em)
            fontSize(1.15.em)
            fontWeight(700)
            lineHeight(1.6.cssRem)
            overflow("hidden")
            property("display", "-webkit-box")
            property("-webkit-line-clamp", "2")
            property("-webkit-box-orient", "vertical")
        }

        ".news-card-title a" style {
            color(Color("#f1f5f9"))
            textDecoration("none")
            property("transition", "color 0.2s ease")
        }

        ".news-card-title a:hover" style {
            color(Color("#60a5fa"))
        }

        ".news-card-category" style {
            padding(0.3.em, 0.9.em)
            borderRadius(999.px)
            fontSize(0.75.em)
            fontWeight(600)
            property("text-transform", "uppercase")
            whiteSpace("nowrap")
            property("letter-spacing", "0.05em")
        }

        ".category-trade" style {
            backgroundColor(Color("#3b82f6"))
            color(Color.white)
        }

        ".category-signing" style {
            backgroundColor(Color("#8b5cf6"))
            color(Color.white)
        }

        ".category-other" style {
            backgroundColor(Color("#94a3b8"))
            color(Color.white)
        }

        ".news-card-description" style {
            color(Color("#94a3b8"))
            lineHeight(1.7.cssRem)
            marginBottom(1.em)
            fontSize(0.95.em)
            flex(1)
            overflow("hidden")
            property("display", "-webkit-box")
            property("-webkit-line-clamp", "4")
            property("-webkit-box-orient", "vertical")
        }

        ".news-card-footer" style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            fontSize(0.85.em)
            color(Color("#64748b"))
            property("margin-top", "auto")
            paddingTop(1.em)
            gap(0.5.em)
            flexWrap(FlexWrap.Wrap)
        }

        ".news-card-date" style {
            fontWeight(500)
        }

        ".news-card-meta" style {
            fontStyle("italic")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.5.em)
        }

        ".translation-badge" style {
            fontSize(0.9.em)
            opacity(0.8)
        }
    }
}